// packages/core/src/learning/schools/boglehead-indexing.ts
// ─────────────────────────────────────────────────────────────────────────
// WORKED TEMPLATE. This is the reference exhibit — author every other school
// to match this shape, depth, and voice. Copy this file, rename, refill.
//
// Voice reminder: neutral enough to be trustworthy, opinionated enough to be
// memorable. Frame beliefs as "[School] believes…". Never invent quotes,
// never tell users what to buy, never copy book/creator text.
//
// ID-bound fields (the validator + compiler guard these):
//   - figures:        FigureId[]  — only ids that exist in figureRegistry.ts
//   - applyActions:   ApplyAction union — type-checked at compile time
//   - featuredLessonIds / debateIds — plain strings; validator warns if they
//     don't resolve. Leave [] until the referenced lesson/debate exists.
// ─────────────────────────────────────────────────────────────────────────

import type { SchoolExhibitEntry } from '../SchoolExhibitMeta';

export const BOGLEHEAD_INDEXING_EXHIBIT: SchoolExhibitEntry = {
	id: 'boglehead-indexing',
	tagline: 'Bogleheads believe most people lose by trying to be clever.',
	worldviewSummary:
		'Boglehead indexing sees investing as a humility game. The belief is simple: most people do not need to pick winning stocks, time the market, or turn investing into a second job. Instead, Bogleheads favor broad diversification, low costs, steady contributions, and staying the course through market noise. The worldview is not flashy, but that is the point. It tries to remove the parts of investing where normal people usually hurt themselves.',
	coreBeliefs: [
		'Bogleheads believe most investors are better off owning the market than trying to outsmart it.',
		'Bogleheads believe low fees matter because tiny costs can quietly eat decades of growth.',
		'Bogleheads believe behavior is the real battleground — panic, greed, and impatience do more damage than boring funds.',
		'Bogleheads believe diversification protects you from betting your future on one company, trend, or prediction.',
		'Bogleheads believe time in the market matters more than trying to guess the perfect moment.',
		'Bogleheads believe simple plans are easier to stick with when life gets stressful.',
	],
	warnsAgainst: [
		'Turning investing into a personality test.',
		'Chasing hot stocks, trends, or fund managers because they look smart right now.',
		'Ignoring fees because they look small on paper.',
		'Changing the plan every time the market gets loud.',
	],
	helps: 'This school is most useful for people who want a simple long-term investing worldview without making stock picking their hobby. It fits best when you value consistency, low costs, and behavior control more than excitement.',
	figures: ['john-bogle', 'jl-collins'],
	canon: [
		{
			title: 'The Little Book of Common Sense Investing',
			author: 'John C. Bogle',
			oneLineIdea:
				'Most investors do not need to hunt for winners when they can own a broad slice of the market at low cost.',
		},
		{
			title: 'The Simple Path to Wealth',
			author: 'JL Collins',
			oneLineIdea:
				'A simple investing plan can reduce decision fatigue and keep your money focused on long-term compounding.',
		},
		{
			title: 'The three-fund portfolio',
			oneLineIdea:
				'A basic mix of broad stock and bond funds can give investors diversification without turning investing into a maze.',
		},
		{
			title: 'Stay the course',
			oneLineIdea:
				'The hard part is not knowing the plan — it is staying with the plan when markets test your behavior.',
		},
		{
			title: 'Buy the haystack',
			oneLineIdea:
				'Instead of searching for the few winning needles, Bogleheads prefer owning the whole haystack.',
		},
	],
	featuredLessonIds: [],
	nativeModuleId: 'school-boglehead-indexing',
	debateIds: [],
	// ─────────────────────────────────────────────────────────────────────────
	// REPLACEMENT for the applyActions array in
	//   packages/core/src/learning/schools/boglehead-indexing.ts
	//
	// WHY: the original used runFreedomCost and calculateFiNumber. Both route to
	// /tools (banned, hidden). Swapped for live, school-surface-legal actions that
	// preserve the same intent. Shape is the correct SchoolApplyAction:
	//   { label, action: { ...ApplyAction } }  — outer label + nested action.
	// No lessonId anywhere (wired in the cross-reference pass).
	// ─────────────────────────────────────────────────────────────────────────

	applyActions: [
		{
			label: 'See what your savings rate does to your freedom date',
			action: {
				type: 'analyzeSavingsRate',
				label: 'Check your savings rate',
			},
		},
		{
			label: 'Test how steady investing changes your future',
			action: {
				type: 'openSimulator',
				label: 'Simulate steady investing',
				scenarioType: 'custom',
			},
		},
		{
			label: 'Build a freedom plan around owning the whole market',
			action: {
				type: 'buildFreedomPlan',
				label: 'Build a freedom plan',
			},
		},
		{
			label: 'Write your boring investing rules before the market gets loud',
			action: {
				type: 'noToolAction',
				label: 'Write your investing rules',
				prompt: 'Write three rules you would follow during a market drop: how often you invest, what would make you change the plan, and what behavior you are trying to avoid.',
			},
		},
	],
	marketingHooks: [
		{
			line: 'Bogleheads believe most people lose by trying to be clever with investing.',
			kind: 'hook',
		},
		{
			line: 'What did you learn after trying to pick stocks that a simple index plan would have avoided?',
			kind: 'interview-question',
		},
		{
			line: 'Nisk teaches the worldview behind boring investing — then lets users test the long-term math on their own numbers.',
			kind: 'kickstarter-beat',
		},
		{
			line: 'Walk your audience through what happens when fees, panic, and stock picking quietly drag down a young investor.',
			kind: 'creator-prompt',
		},
		{
			line: 'The Boglehead move is simple: own the market, lower the noise, and stop making investing your ego project.',
			kind: 'hook',
		},
		{
			line: 'Where do you think simple indexing gets misunderstood by beginners?',
			kind: 'interview-question',
		},
		{
			line: 'This school helps users see why the boring plan can be powerful when behavior is the real risk.',
			kind: 'kickstarter-beat',
		},
		{
			line: 'Make a video comparing a complicated portfolio against a simple low-cost index approach over 30 years.',
			kind: 'creator-prompt',
		},
	],
	revision: 1,
};
